// File: C:\Users\s-naidu\Desktop\merchant-card\frontend\src\merchant\pages\forms\Form1CompanyOverview.tsx

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import MerchantLayout from "../../components/MerchantLayout";

type FormData = {
  company_name: string;
  company_name_kana: string;

  postal_code: string;
  address_city: string;
  address_rest: string;

  phone: string;
  fax: string;

  established_date: string;

  industry: string;
  industry_other: string;

  capital: string;
  store_count: string;
  employee_count: string;
  business_hours: string;
  regular_holiday: string;

  is_completed: boolean;
};

const API_BASE = "/api";

const empty: FormData = {
  company_name: "",
  company_name_kana: "",
  postal_code: "",
  address_city: "",
  address_rest: "",
  phone: "",
  fax: "",
  established_date: "",
  industry: "",
  industry_other: "",
  capital: "",
  store_count: "",
  employee_count: "",
  business_hours: "",
  regular_holiday: "",
  is_completed: false,
};

export default function Form1CompanyOverview() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [data, setData] = useState<FormData>(empty);

  const token = localStorage.getItem("merchant_access_token") || "";

  const setField = (key: keyof FormData, val: string | boolean) => {
    setData((prev) => ({ ...prev, [key]: val } as FormData));
  };

  

// 👇 ADD THIS EXACTLY HERE
const formatPhoneNumber = (value: string) => {
  const digits = value.replace(/\D/g, "").slice(0, 11);

  if (digits.length <= 3) return digits;

  if (digits.length <= 10) {
    if (digits.startsWith("03") || digits.startsWith("06")) {
      if (digits.length <= 6) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
      return `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    if (digits.length <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
};



const formatYearMonth = (value: string) => {
  const cleaned = value.replace(/[^0-9\-\/]/g, "");

  // Replace "-" with "/"
  const normalized = cleaned.replace(/-/g, "/");

  // Limit to YYYY/MM format (7 chars max)
  return normalized.slice(0, 7);
};





  // ✅ Completion rule (required fields filled => 入力済み)
  const canComplete = useMemo(() => {
    return (
      data.company_name.trim() !== "" &&
      data.company_name_kana.trim() !== "" &&
      data.postal_code.trim() !== "" &&
      data.address_city.trim() !== "" &&
      data.address_rest.trim() !== "" &&
      data.phone.trim() !== "" &&
      data.established_date.trim() !== "" &&
      data.industry.trim() !== "" &&
      (data.industry !== "その他" || data.industry_other.trim() !== "")
    );
  }, [data]);

  // ✅ Step③ status label
  const statusLabel = canComplete ? "入力済み" : "未完了";

  useEffect(() => {
    const run = async () => {
      setError("");
      setSuccess("");

      if (!token) {
        setError("ログイン情報がありません。再ログインしてください。");
        setLoading(false);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/merchant/forms/company-overview`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const json = await res.json().catch(() => ({}));

        if (res.status === 401) {
          setError(json?.detail || "認証が切れました。再ログインしてください。");
          setLoading(false);
          return;
        }

        if (!res.ok) {
          setError(json?.detail || "フォームデータの取得に失敗しました。");
          setLoading(false);
          return;
        }

        const d = json?.data || {};

        setData((prev) => ({
          ...prev,
          company_name: d.company_name || "",
          company_name_kana: d.company_name_kana || "",
          postal_code: d.postal_code || "",
          address_city: d.address_city || "",
          address_rest: d.address_rest || "",
          phone: d.phone || "",
          fax: d.fax || "",
          established_date: d.established_date || "",
          industry: d.industry || "",
          industry_other: d.industry_other || "",
          capital: d.capital || "",
          store_count: d.store_count || "",
          employee_count: d.employee_count || "",
          business_hours: d.business_hours || "",
          regular_holiday: d.regular_holiday || "",
          is_completed: Boolean(d.is_completed),
        }));

        setLoading(false);
      } catch {
        setError(
          "フォームデータの取得に失敗しました。バックエンドが起動しているか確認してください。"
        );
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

    const onSave = async (): Promise<boolean> => {
    setError("");
    setSuccess("");
    setSaving(true);

    if (!token) {
      setError("ログイン情報がありません。再ログインしてください。");
      setSaving(false);
      return false;
    }

    try {
      const payload = {
        ...data,
        // ✅ Always compute completion from required-fields rule
        is_completed: canComplete,
      };

      const res = await fetch(`${API_BASE}/merchant/forms/company-overview`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json().catch(() => ({}));

      if (res.status === 401) {
        setError(json?.detail || "認証が切れました。再ログインしてください。");
        setSaving(false);
        return false;
      }

      if (!res.ok) {
        setError(json?.detail || "保存に失敗しました。");
        setSaving(false);
        return false;
      }

      // ✅ Keep DB value in state too
      setField("is_completed", canComplete);

      // ✅ Step③: wording aligned to status labels
      setSuccess(canComplete ? "保存しました（入力済み）" : "保存しました（未完了）");
      setSaving(false);
      return true;
    } catch {
      setError("保存に失敗しました。バックエンドが起動しているか確認してください。");
      setSaving(false);
      return false;
    }
  };



    const onNext = async () => {
    const ok = await onSave();
    if (!ok) return;
    navigate("/merchant/forms/representative");
  };

  const onList = async () => {
  const ok = await onSave();
  if (!ok) return;
  navigate("/merchant/register");
};


  

  // ✅ Step③: Save button color rule
  const saveButtonBg = canComplete ? "#dc2626" : "#0b1f3b"; // red / blue

  return (
    <MerchantLayout title="1. 会社概要（全業種共通）" active="forms">
      {loading && <div style={{ color: "#475569" }}>読み込み中...</div>}

      {error && (
        <div
          style={{
            marginBottom: 12,
            background: "#fff1f2",
            border: "1px solid #fecdd3",
            color: "#b91c1c",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 800,
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            marginBottom: 12,
            background: "#ecfdf5",
            border: "1px solid #bbf7d0",
            color: "#166534",
            padding: "10px 12px",
            borderRadius: 12,
            fontWeight: 900,
          }}
        >
          {success}（いつでも編集できます）
        </div>
      )}

      {!loading && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)",
              gap: 20,
              alignItems: "start",
            }}
          >
            <Input label="会社名（全角・編集可）" required>
  <input
    style={input}
    value={data.company_name}
    onChange={(e) => setField("company_name", e.target.value)}
    placeholder="例）株式会社〇〇（全角）"
  />
</Input>

            <Input label="会社名（カナ・半角）" required>
  <input
    style={input}
    value={data.company_name_kana}
    onChange={(e) => setField("company_name_kana", e.target.value)}
    placeholder="例）ｶﾌﾞｼｷｶﾞｲｼｬ○○（半角ｶﾅ）"
  />
</Input>

            <Input label="郵便番号" required>
  <input
    style={input}
    value={data.postal_code}
    onChange={async (e) => {
      const value = e.target.value.replace(/\D/g, "").slice(0, 7);
      setField("postal_code", value);

      // ✅ Call API when 7 digits entered
      if (value.length === 7) {
  try {
    const res = await fetch(`${API_BASE}/postal/${value}`);
    const json = await res.json();

    if (json?.ok && json?.data) {
      setField("address_city", json.data.address_city || "");
      setField("address_rest", json.data.address_rest || "");
    }
  } catch (err) {
    console.error("Postal lookup failed", err);
  }
}
    }}
    placeholder="例）1234567"
  />
</Input>

            <Input label="電話番号" required>
  <input
    style={input}
    value={data.phone}
    onChange={(e) => setField("phone", formatPhoneNumber(e.target.value))}
    placeholder="例）03-1234-5678"
  />
</Input>

            <Input label="登記所在地（都道府県・市区町村・全角）" required>
  <input
    style={input}
    value={data.address_city}
    onChange={(e) => setField("address_city", e.target.value)}
    placeholder="例）東京都渋谷区（全角）"
  />
</Input>

            <Input label="FAX番号">
              <input
                style={input}
                value={data.fax}
                onChange={(e) => setField("fax", e.target.value)}
                placeholder="例）0312345679"
              />
            </Input>

            <Input label="登記所在地（番地以降・全角）" required>
  <input
    style={input}
    value={data.address_rest}
    onChange={(e) => setField("address_rest", e.target.value)}
    placeholder="例）〇〇１−２−３ 〇〇ビル１０Ｆ（全角）"
  />
</Input>

            <Input label="会社設立年月（YYYY/MM）" required>
  <input
    style={input}
    value={data.established_date}
    onChange={(e) =>
      setField("established_date", formatYearMonth(e.target.value))
    }
    placeholder="例）2020/04"
  />
</Input>

            <Input label="業種（スタッフ設定のため編集不可）" required>
  <select
    style={{
      ...input,
      background: "#f1f5f9",
      color: "#475569",
      cursor: "not-allowed",
    }}
    value={data.industry}
    disabled
  >
    <option value="">選択してください</option>
    <option value="美容医療">美容医療</option>
    <option value="エステティック">エステティック</option>
    <option value="BtoB">BtoB</option>
    <option value="異業種">異業種</option>
    <option value="その他">その他</option>
  </select>
</Input>

            <Input label="業種（その他）" required={data.industry === "その他"}>
              <input
                style={{
                  ...input,
                  background: data.industry === "その他" ? "#fff" : "#f1f5f9",
                }}
                value={data.industry_other}
                onChange={(e) => setField("industry_other", e.target.value)}
                placeholder="その他の場合のみ入力"
                disabled={data.industry !== "その他"}
              />
            </Input>

            <Input label="資本金">
  <InputWithUnit unit="万円">
    <input
      style={unitInput}
      value={data.capital}
      onChange={(e) => setField("capital", e.target.value)}
      placeholder="例）1000"
    />
  </InputWithUnit>
</Input>

            <Input label="店舗数">
  <InputWithUnit unit="店舗">
    <input
      style={unitInput}
      value={data.store_count}
      onChange={(e) => setField("store_count", e.target.value)}
      placeholder="例）3"
    />
  </InputWithUnit>
</Input>

           <Input label="従業員数">
  <InputWithUnit unit="人">
    <input
      style={unitInput}
      value={data.employee_count}
      onChange={(e) => setField("employee_count", e.target.value)}
      placeholder="例）20"
    />
  </InputWithUnit>
</Input>

            <Input label="営業時間">
              <input
                style={input}
                value={data.business_hours}
                onChange={(e) => setField("business_hours", e.target.value)}
                placeholder="例）10:00〜19:00"
              />
            </Input>

            <Input label="定休日">
              <input
                style={input}
                value={data.regular_holiday}
                onChange={(e) => setField("regular_holiday", e.target.value)}
                placeholder="例）水曜日"
              />
            </Input>
          </div>

          {/* ✅ Step③: only ONE button on the form page */}
                        <div
            style={{
              marginTop: 18,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
            }}
          >
            <button
              onClick={onSave}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#1d4ed8",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "保存"}
            </button>


            <button
  onClick={onList}
  disabled={saving}
  style={{
    padding: "12px 16px",
    borderRadius: 12,
    border: "none",
    background: "#64748b",
    color: "#fff",
    fontWeight: 900,
    cursor: saving ? "not-allowed" : "pointer",
    opacity: saving ? 0.7 : 1,
    transition: "background 0.2s ease",
  }}
>
  一覧
</button>

            <button
              onClick={onNext}
              disabled={saving}
              style={{
                padding: "12px 16px",
                borderRadius: 12,
                border: "none",
                background: "#dc2626",
                color: "#fff",
                fontWeight: 900,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.7 : 1,
                transition: "background 0.2s ease",
              }}
            >
              {saving ? "保存中..." : "次へ"}
            </button>
          </div>

          <div style={{ marginTop: 10, fontSize: 13, color: "#475569" }}>
            ステータス：{" "}
            <span
              style={{
                fontWeight: 900,
                color: canComplete ? "#dc2626" : "#0b1f3b",
              }}
            >
              {statusLabel}
            </span>
          </div>

          {!canComplete && (
            <div style={{ fontSize: 12, color: "#475569", fontWeight: 800 }}>
              ※ 必須項目が未入力のため「未完了」になります
            </div>
          )}      
        </>
      )}
    </MerchantLayout>
  );
}

function Input({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 900, marginBottom: 6, color: "#0f172a" }}>
        {label} {required ? <span style={{ color: "#b91c1c" }}>（必須）</span> : null}
      </div>
      {children}
    </div>
  );
}










/* ADD HERE */
function InputWithUnit({
  unit,
  children,
}: {
  unit: string;
  children: React.ReactNode;
}) {
  return (
    <div style={unitWrap}>
      {children}
      <span style={unitText}>{unit}</span>
    </div>
  );
}




const input: React.CSSProperties = {
  width: "100%",
  height: 44,
  padding: "0 12px",
  borderRadius: 10,
  border: "1px solid #cbd5e1",
  background: "#fff",
  color: "#0f172a",
  fontSize: 14,
  outline: "none",
  boxSizing: "border-box",
};


const unitWrap: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  border: "1px solid #cbd5e1",
  borderRadius: 10,
  background: "#fff",
  overflow: "hidden",
};

const unitInput: React.CSSProperties = {
  ...input,
  border: "none",
  borderRadius: 0,
  flex: 1,
};

const unitText: React.CSSProperties = {
  padding: "0 12px",
  height: 44,
  display: "flex",
  alignItems: "center",
  borderLeft: "1px solid #e2e8f0",
  background: "#f8fafc",
  color: "#334155",
  fontSize: 14,
  fontWeight: 900,
  whiteSpace: "nowrap",
};
